/**
 * The platform console's gateways (docs/plans/2026-10-18_remote-gateway-console.md, P0): every gateway across orgs,
 * one gateway's details, its remote ops (one BLE request carried over the cloud command channel, ADR 0002 A22) and
 * their history. A platform admin only (RequirePlatformAdmin on /v1/admin/*; rule R8): the org filter only narrows
 * the view. Mirrors admin.GatewayRow / GatewayDetail / OpResult / OpHistoryItem (platform-core/internal/admin
 * gateway_console.go and gateway_ops.go).
 */

import { api } from "@/lib/api-client";
import type { GatewayCapabilities } from "./types";

/** The network that carries a gateway's default route, from its birth's networks. */
export interface AdminGatewayUplink {
  iface: string;
  kind: string;
  cidrs: string[];
  gateway?: string;
  ssid?: string;
}

/** A birth's remote_ops: the BLE opcodes the gateway takes over the cloud's op command. */
export interface AdminGatewayRemoteOps {
  v: 1;
  opcodes: number[];
}

/** One gateway in the console's list. */
export interface AdminGatewayRow {
  id: string;
  org_id: string;
  org_name: string;
  site_id: string;
  site_name: string;
  slug: string;
  name: string;
  /** The advertised BLE name (SGWP-7E1A90A1, the sticker's); null before the firmware sends it. */
  ble_name: string | null;
  /** The gateway holds a certificate, so it can take commands. */
  claimed: boolean;
  /** The last status message was a birth. */
  online: boolean;
  online_changed_at: string | null;
  last_seen: string | null;
  /** The birth's fw, else the version the gateway last reported. */
  firmware: string | null;
  /** The birth's fw_line: sgwp, sgwmp or sgwe. */
  firmware_line: string | null;
  uplink: AdminGatewayUplink | null;
  remote_ops: AdminGatewayRemoteOps | null;
  birth_at: string | null;
  cert_cn: string | null;
  cert_expires_at: string | null;
  created_at: string;
  updated_at: string;
}

/** GET /v1/admin/gateways. */
export interface AdminGatewayList {
  items: AdminGatewayRow[];
  /** Every gateway that passes the filters, on every page. */
  total: number;
  limit: number;
  /** Deprecated offset paging; 0 when the cursor pages. */
  offset: number;
  next_cursor: string | null;
}

/** An opcode the console may send, with its ADR 0002 name. */
export interface AdminRemoteOpcode {
  opcode: number;
  name: string;
}

/** One of the cloud's instruments under a gateway. */
export interface AdminGatewayDevice {
  id: string;
  name: string;
  slug: string;
  kind: string;
  driver: string | null;
  last_seen: string | null;
}

/** GET /v1/admin/gateways/{gw_id}. */
export interface AdminGatewayDetail extends AdminGatewayRow {
  /** The last birth exactly as the gateway sent it; null when it never sent one. */
  capabilities: GatewayCapabilities | null;
  /** The ops the console may send now: the cloud's remote reads that the gateway's remote_ops lists. */
  allowed_ops: AdminRemoteOpcode[];
  devices: AdminGatewayDevice[];
}

/** A gateway's answer to an op, exactly as BLE returns it; an ERROR answer (0x80FF) too. */
export interface AdminGatewayOpAnswer {
  opcode: number;
  name?: string;
  body: unknown;
}

/** POST /v1/admin/gateways/{gw_id}/ops when the gateway answered. */
export interface AdminGatewayOpResult {
  id: string;
  gateway_id: string;
  opcode: number;
  opcode_name: string;
  status: "applied";
  answer: AdminGatewayOpAnswer;
  took_ms: number;
  created_at: string;
}

export type AdminGatewayOpStatus = "sent" | "applied" | "rejected" | "error" | "timeout" | "refused" | "failed";

/** One remote op in a gateway's history; its body and answer have their secrets removed. */
export interface AdminGatewayOp {
  id: string;
  opcode: number;
  opcode_name: string;
  actor_sub: string;
  actor_email: string | null;
  actor_name: string | null;
  body: Record<string, unknown>;
  status: AdminGatewayOpStatus;
  detail: string;
  answer: AdminGatewayOpAnswer | null;
  took_ms: number | null;
  created_at: string;
  finished_at: string | null;
}

/** GET /v1/admin/gateways/{gw_id}/ops. */
export interface AdminGatewayOpList {
  items: AdminGatewayOp[];
  next_cursor: string | null;
}

/** The list's server-side filters. Empty arrays and strings mean no filter. */
export interface AdminGatewayQuery {
  orgIds?: string[];
  siteIds?: string[];
  q?: string;
  online?: boolean | null;
  line?: string;
  limit?: number;
  cursor?: string | null;
}

/** The list's query string: org_id and site_id repeat, the rest once; nothing when there is no filter. */
export function adminGatewayQueryString(q: AdminGatewayQuery): string {
  const p = new URLSearchParams();
  for (const id of q.orgIds ?? []) p.append("org_id", id);
  for (const id of q.siteIds ?? []) p.append("site_id", id);
  if (q.q?.trim()) p.set("q", q.q.trim());
  if (typeof q.online === "boolean") p.set("online", String(q.online));
  if (q.line?.trim()) p.set("line", q.line.trim());
  if (q.limit) p.set("limit", String(q.limit));
  if (q.cursor) p.set("cursor", q.cursor);
  const s = p.toString();
  return s ? `?${s}` : "";
}

const path = (id: string) => `/v1/admin/gateways/${encodeURIComponent(id)}`;

export const adminGatewaysApi = {
  /** One page of the gateways across orgs, filtered on the server. */
  list: (q: AdminGatewayQuery = {}) => api.get<AdminGatewayList>(`/v1/admin/gateways${adminGatewayQueryString(q)}`),
  /** One gateway: its row, its last birth, the ops it takes now and its instruments. */
  get: (id: string) => api.get<AdminGatewayDetail>(path(id)),
  /**
   * Send one BLE request to the gateway and wait for its answer (10 s, 15 s for HEALTH_CHECK and PING). Throws the
   * API's refusal: 409 offline or not claimed, 422 not a remote read / not offered / refused by the gateway, 429, 504.
   */
  op: (id: string, opcode: number, body: Record<string, unknown> = {}) =>
    api.post<AdminGatewayOpResult>(`${path(id)}/ops`, { opcode, body }),
  /** The gateway's remote ops, newest first. */
  ops: (id: string, cursor?: string | null, limit?: number) => {
    const p = new URLSearchParams();
    if (limit) p.set("limit", String(limit));
    if (cursor) p.set("cursor", cursor);
    const s = p.toString();
    return api.get<AdminGatewayOpList>(`${path(id)}/ops${s ? `?${s}` : ""}`);
  },
};
